import * as React from 'react'
import TopNavigation from '../components/Navigation/TopNavigation'
import { AppState } from '../store'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import ERoute from '../ERoute'
import { RouteComponentProps } from 'react-router'
import ScrollToTopOnMount from '../components/Effects/ScrollToTopOnMount'
import { showConfirmModal, showHelpCenterModal } from '../store/modals/actions'
import PageContent from '../components/Page/PageContent'
import PageHeader from '../components/Page/PageHeader'
import { Helmet } from 'react-helmet'
import { withTranslation, WithTranslation } from 'react-i18next'
import { HelpCenter, HelpCenterFilter, HelpCenterFilters, CurrentUser, ResourceListFilterType } from '../types'
import LocalStorage, { LocalStorageKey } from '../LocalStorage'
import ResourceTableRow from '../components/Resource/ResourceTableRow'
import ResourceTableRowData from '../components/Resource/ResourceTableRowData'
import ResourceTableRowActions from '../components/Resource/ResourceTableRowActions'
import CardEmptyInfo from '../components/Card/CardEmptyInfo'
import ResourceTable, { ResourceTableAction } from '../components/Resource/ResourceTable'
import PageLoader from '../components/Page/PageLoader'
import { HelpCentersController } from '../controllers'
import RouteHelper from '../helpers/RouteHelper'
import Notification from '../utilities/Notification'
import { LOCALES } from '../Constants'
import Icon from '../components/Icons/Icon'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import Badge from '../components/Badge/Badge'
import SearchHelper from '../helpers/SearchHelper'
import NavigationDetection from '../components/Effects/NavigationDetection'
import Switch from '../components/Form/Switch'

const HelpCenterAvatarAndName = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  >:first-child {
    margin-right: 8px;
  }
`

const HelpCenterNameAndMetadata = styled.div`
  display: flex;
  flex-direction: column;
`

const HelpCenterName = styled(Link)`
  display: flex;
  flex-direction: row;
  font-weight: 500;
  color: black;

  &:hover {
    text-decoration: underline;
  }
`

const HelpCenterMetadata = styled.div`
  display: flex;
  margin-top: -4px;
  color: #333333 !important;

  a {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-left: 4px;

    svg {
      width: 15px;
      height: 15px;
      margin-right: 4px;
    }

    &:hover {
      text-decoration: underline;
    }
  }
`

const HelpCenterDetailsContainer = styled.div`
  display: flex;
  flex-direction: row;
`

const HelpCenterDetails = styled.div`
  display: flex;
  flex-direction: column;
  color: #333333 !important;
  align-items: flex-end;

  a {
    display: flex;
    flex-direction: row;
    align-items: center;

    svg {
      width: 15px;
      height: 15px;
      margin-right: 10px;
      fill: #829AB1;
    }

    &:hover {
      text-decoration: underline;
    }
  }
`

const HelpCenterGroups = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  margin-top: 4px;
`

const StarHelpCenter = styled.div<{ starred: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: ${props => props.starred ? '#f4a230' : 'black'};
  margin-left: 8px;

  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
`

interface IStateProps extends RouteComponentProps<{ groupId?: string }> {
	currentUser: CurrentUser
}

interface IDispatchToProps {
	showHelpCenterModal: typeof showHelpCenterModal
	showConfirmModal: typeof showConfirmModal
}

interface IState {
	helpCenters: HelpCenter[],
	page: number,
	perPage: number,
	totalPages: number
	didInitialLoad: boolean
	isFetching: boolean
	sortValue: string
	activeFilters: any,
	selectedHelpCenterIds: string[]
	search: string
}

type IProps = IStateProps & IDispatchToProps & WithTranslation

class HelpCenters extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props)

		const { search, page, perPage, filters } = SearchHelper.getSearch(this.props.location.search, HelpCenterFilters)

		this.state = {
			helpCenters: [],
			page: page,
			perPage: perPage,
			totalPages: 0,
			didInitialLoad: false,
			isFetching: false,
			sortValue: LocalStorage.get(LocalStorageKey.HELP_CENTER_SORT_VALUE, '-'),
			selectedHelpCenterIds: [],
			search: search,
			activeFilters: filters,
		}

		this.onRouteChange = this.onRouteChange.bind(this)
		this.onNewHelpCenterClick = this.onNewHelpCenterClick.bind(this);
		this.onTableHelpCenterClick = this.onTableHelpCenterClick.bind(this)
		this.onTableActionClick = this.onTableActionClick.bind(this)
		this.onTableSelectionChange = this.onTableSelectionChange.bind(this)
		this.onTableRowSelectionChange = this.onTableRowSelectionChange.bind(this)
		this.onTableHelpCenterDeleteClick = this.onTableHelpCenterDeleteClick.bind(this);
		this.onHelpCenterFormSubmit = this.onHelpCenterFormSubmit.bind(this)
		this.onHelpCenterUpdateFormSubmit = this.onHelpCenterUpdateFormSubmit.bind(this)
		this.onHelpCenterCreate = this.onHelpCenterCreate.bind(this)
		this.onHelpCenterFiltersChange = this.onHelpCenterFiltersChange.bind(this)
		this.onHelpCenterSortValueChange = this.onHelpCenterSortValueChange.bind(this)
		this.onHelpCenterSearchChange = this.onHelpCenterSearchChange.bind(this)
		this.onHelpCenterSearchSubmit = this.onHelpCenterSearchSubmit.bind(this)
		this.onHelpCenterClearFilters = this.onHelpCenterClearFilters.bind(this)
		this.onBulkDeleteClick = this.onBulkDeleteClick.bind(this)
		this.onPageChange = this.onPageChange.bind(this)
		this.onPerPageChange = this.onPerPageChange.bind(this)
		this.onToggleEnabledChange = this.onToggleEnabledChange.bind(this)
	}

	onRouteChange() {
		const { search, page, perPage, filters } = SearchHelper.getSearch(this.props.location.search, HelpCenterFilters)

		this.setState({
			search: search,
			page: page,
			perPage: perPage,
			activeFilters: filters
		}, () => {
			this.fetchHelpCenters(page)
		})
	}

	async fetchHelpCenters(page: number) {
		const { search, sortValue, activeFilters, perPage } = this.state
		this.setState({
			isFetching: true
		}, async () => {
			try {
				const response = await HelpCentersController.getHelpCenters({
					page: page,
					search: search,
					order: `${sortValue}`,
					per_page: perPage,
					...activeFilters
				})
				const { help_centers, current_page, total_pages, total_entries } = response;

				this.setState({
					helpCenters: [...help_centers],
					page: current_page,
					totalPages: total_pages,
					didInitialLoad: true,
					isFetching: false
				});
			} catch (ex) {
				console.error(ex)
			}
		});
	}

	onNewHelpCenterClick(e: React.MouseEvent<HTMLAnchorElement>) {
		e.preventDefault()

		const { showHelpCenterModal } = this.props

		showHelpCenterModal({
			helpCenter: {},
			onSubmit: this.onHelpCenterFormSubmit,
		})
	}

	onTableHelpCenterClick(helpCenter: HelpCenter) {
		this.props.history.push(RouteHelper.process(ERoute.PATH_HELP_CENTER, { id: helpCenter.id }))
	}

	onTableActionClick(key: string, helpCenter: HelpCenter) {
		switch (key) {
			case 'delete': this.onTableHelpCenterDeleteClick(helpCenter)
				break
			default:
				throw Error('[HelpCenters] Unimplemented onTableActionClick')
		}
	}

	onTableSelectionChange(selectedHelpCenterIds: string[]) {
		this.setState({ selectedHelpCenterIds: selectedHelpCenterIds })
	}

	onTableRowSelectionChange(selected: boolean, transactionId: string) {
		const { selectedHelpCenterIds } = this.state

		if (selected) {
			this.setState({ selectedHelpCenterIds: [...selectedHelpCenterIds, transactionId] })
		} else {
			this.setState({ selectedHelpCenterIds: selectedHelpCenterIds.filter(selectedTransactionId => selectedTransactionId !== transactionId) })
		}
	}

	onTableHelpCenterDeleteClick(helpCenter: HelpCenter) {
		const { showConfirmModal, t } = this.props

		showConfirmModal({
			title: t('HelpCenter::Delete help center'),
			description: t('HelpCenter::You are about to delete {{name}}. By deleting this help center you are also deleting all its associated data. Are you sure?', { name: helpCenter.name }),
			action: { label: t('HelpCenter::Delete'), isDestructive: true },
			onConfirm: () => {
				HelpCentersController
					.delete(helpCenter.id)
					.then((response) => {
						if (response.errors) {

						} else {
							const { helpCenters } = this.state;

							const helpCenterIndex = helpCenters.findIndex(c => c.id === helpCenter.id);

							helpCenters.splice(helpCenterIndex, 1);

							this.setState({
								helpCenters: helpCenters
							});

							Notification.notifySuccess(t('HelpCenter::Help center successfully deleted'))
						}
					})
					.catch(console.error)
			}
		})
	}

	onHelpCenterFormSubmit(helpCenter: HelpCenter) {
		const { helpCenters } = this.state;

		const newHelpCenters = [helpCenter, ...helpCenters];

		this.setState({ helpCenters: newHelpCenters });
	}

	onHelpCenterUpdateFormSubmit(helpCenter: HelpCenter) {
		const { helpCenters } = this.state

		const helpCenterIndex = helpCenters.findIndex(c => c.id === helpCenter.id);

		if (helpCenterIndex !== -1) {
			helpCenters[helpCenterIndex] = helpCenter
		}

		this.setState({
			helpCenters: [
				...helpCenters,
			]
		})
	}

	onHelpCenterCreate(data) {
		const { helpCenters } = this.state
		const { detail: { helpCenter } } = data

		this.setState({
			helpCenters: [
				helpCenter,
				...helpCenters,
			]
		})
	}

	onHelpCenterFiltersChange(activeFilters: any) {
		this.setState({ page: 1, activeFilters: activeFilters }, () => {
			const { page, activeFilters, search, sortValue } = this.state
			RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
		})
	}

	onHelpCenterSortValueChange(value: string) {
		LocalStorage.set(LocalStorageKey.HELP_CENTER_SORT_VALUE, value)
		this.setState({ page: 1, sortValue: value }, () => {
			const { page, activeFilters, search, sortValue } = this.state
			RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
		})
	}

	onHelpCenterSearchChange(search) {
		this.setState({ search: search })
	}

	onHelpCenterSearchSubmit(search) {
		this.setState({ page: 1, search: search }, () => {
			const { page, activeFilters, search, sortValue } = this.state
			RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
		})
	}

	onHelpCenterClearFilters() {
		this.setState({
			page: 1,
			search: '',
			activeFilters: {}
		}, () => {
			const { page, activeFilters, search, sortValue } = this.state
			RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
		})
	}

	onBulkDeleteClick() {
		const { showConfirmModal, t } = this.props
		const { helpCenters, selectedHelpCenterIds } = this.state

		showConfirmModal({
			title: t('HelpCenter::Delete help centers'),
			description: t('HelpCenter::You are about to delete {{count}} help centers. By deleting these help centers you are also deleting all their associated data. Are you sure?', { count: selectedHelpCenterIds.length }),
			action: { label: t('HelpCenter::Delete'), isDestructive: true },
			onConfirm: async () => {
				try {
					await HelpCentersController.bulkDelete(selectedHelpCenterIds)

					this.setState({
						selectedHelpCenterIds: [],
						helpCenters: helpCenters.filter(helpCenter => !selectedHelpCenterIds.includes(helpCenter.id))
					})
				} catch (ex) {
					console.error(ex)
				}
			}
		})
	}

	onPageChange(page: number) {
		this.setState({ page: page }, () => {
			const { page, perPage, activeFilters, search, sortValue } = this.state
			RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, perPage: perPage, search: search, ...activeFilters, sortValue: sortValue })
		})
	}

	onPerPageChange(perPage: number) {
		this.setState({ page: 1, perPage: perPage }, () => {
			const { page, perPage, activeFilters, search, sortValue } = this.state
			LocalStorage.set(LocalStorageKey.PER_PAGE, perPage)
			RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, perPage: perPage, search: search, ...activeFilters, sortValue: sortValue })
		})
	}

	async onToggleEnabledChange(e: any, helpCenter: HelpCenter) {
		e.preventDefault()
		e.stopPropagation()
		const { helpCenters } = this.state

		try {
			const enabled = !helpCenter.enabled
			this.setState({ helpCenters: helpCenters.map(c => c.id === helpCenter.id ? { ...c, enabled: enabled } : c) })
			await HelpCentersController.update({ ...helpCenter, enabled: enabled })
		} catch (ex) {
			console.error(ex)
		}
	}

	render() {
		const { currentUser, t } = this.props
		const { helpCenters, didInitialLoad, isFetching, activeFilters, sortValue, search, page, perPage, totalPages, selectedHelpCenterIds } = this.state
		const { workspace: { setting } } = currentUser

		const activeFiltersCount = search?.length > 0 || Object.keys(activeFilters).length > 0
		const promotedBulkActions: ResourceTableAction[] = [
			{ icon: 'trash-alt-solid', content: t('HelpCenters::Delete'), onAction: this.onBulkDeleteClick, destructive: true }
		]

		const bulkActions: ResourceTableAction[] = []
		const filters: HelpCenterFilter[] = [
			{ name: 'name', label: t('HelpCenter::Name'), type: ResourceListFilterType.STRING },
			{ name: 'enabled', label: t('HelpCenter::Enabled'), type: ResourceListFilterType.SINGLE_OPTION, options: [{ label: t('HelpCenters::Yes'), value: String(true) }, { label: t('HelpCenters::No'), value: String(false) }] },
			{ name: 'created_at', label: t('HelpCenter::Created date'), type: ResourceListFilterType.DATE },
		]

		return (
			<>
				<Helmet>
					<title>{t('HelpCenters::{{__appName}} | Help centers')}</title>
				</Helmet>
				<ScrollToTopOnMount />
				<NavigationDetection onRouteChange={this.onRouteChange} />
				<TopNavigation
					icon='book'
					title={t('HelpCenters::Help centers')}
					action={
						<a key='new-project' href='javascript://' className='button button-primary page-action' onClick={this.onNewHelpCenterClick}>
							<Icon icon='plus' />
							{t('HelpCenters::New help center')}
						</a>
					}
				/>

				<PageContent>
					<PageHeader
						title={t('HelpCenters::Help Centers')}
					/>

					{!didInitialLoad && <PageLoader />}
					{didInitialLoad && <ResourceTable
						data={helpCenters}
						headers={[
							{ title: t('HelpCenters::Name') },
							{ title: t('HelpCenters::Enabled') },
							{ title: '', stickyRight: '0px' },
						]}
						renderRow={(helpCenter: HelpCenter) => {
							return (
								<ResourceTableRow
									key={helpCenter.id}
									selected={selectedHelpCenterIds.includes(helpCenter.id)}
									onSelectionChange={(selected) => this.onTableRowSelectionChange(selected, helpCenter.id)}
								>
									<ResourceTableRowData onClick={() => this.onTableHelpCenterClick(helpCenter)} maxWidth='500px' ellipse>
										{helpCenter.name}
									</ResourceTableRowData>
									<ResourceTableRowData onClick={() => this.onTableHelpCenterClick(helpCenter)} ellipse>
										<Switch
											name='enabled'
											checked={helpCenter.enabled}
											onClick={(e) => this.onToggleEnabledChange(e, helpCenter)}
										/>
									</ResourceTableRowData>
									<ResourceTableRowActions
										actions={[
											{ key: 'delete', icon: 'trash-alt-solid', content: t('HelpCenter::Delete'), destructive: true }
										]}
										onActionClick={(key) => this.onTableActionClick(key, helpCenter)}
										sticky={true}
										stickyRight='0px'
									/>
								</ResourceTableRow>
							)
						}}
						renderEmpty={<CardEmptyInfo
							icon={activeFiltersCount ? 'search' : 'book'}
							description={activeFiltersCount ? t('HelpCenter::No help centers found') : t('HelpCenter::No help centers have been created yet')}
							descriptionActionText={activeFiltersCount ? t('HelpCenter::Clear filters') : t('HelpCenter::Add new help center')}
							onDescriptionActionClick={activeFiltersCount ? this.onHelpCenterClearFilters : this.onNewHelpCenterClick}
						/>}
						filters={filters}
						activeFilters={activeFilters}
						onFiltersChange={this.onHelpCenterFiltersChange}
						sortOptions={[
							{ label: '-', value: '-' },
							{ label: t('HelpCenter::Name (A-Z)'), value: 'name_asc' },
							{ label: t('HelpCenter::Name (Z-A)'), value: 'name_desc' },
							{ label: t('HelpCenter::Created at ↑'), value: 'created_at_asc' },
							{ label: t('HelpCenter::Created at ↓'), value: 'created_at_desc' },
						]}
						promotedBulkActions={promotedBulkActions}
						bulkActions={bulkActions}
						sortValue={sortValue}
						onSortChange={this.onHelpCenterSortValueChange}
						pagination={{ page: page, perPage: perPage, pageCount: totalPages }}
						onPageChange={this.onPageChange}
						onPerPageChange={this.onPerPageChange}
						isLoading={isFetching}
						stickyHeader={true}
						selectedItems={selectedHelpCenterIds}
						onSelectionChange={this.onTableSelectionChange}
						searchValue={search}
						onSearchChange={this.onHelpCenterSearchChange}
						onSearchSubmit={this.onHelpCenterSearchSubmit}
						maxHeight='60vh'
					/>}
				</PageContent>
			</>
		)
	}
}

interface IStateToProps {
	currentUser: CurrentUser
}

const mapStateToProps = (state: AppState): IStateToProps => {
	const {
		authentication: {
			currentUser,
		}
	} = state

	return {
		currentUser: currentUser,
	}
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
	return {
		showHelpCenterModal: (options) => dispatch(showHelpCenterModal(options)),
		showConfirmModal: (options) => dispatch(showConfirmModal(options)),
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(HelpCenters))